const equalizers = new WeakMap();

/**
 * Equalizes one or more target elements per card across a product grid, so
 * matching elements in the same visual row share the tallest member's height.
 * Each selector is equalized independently against a shared row-grouping pass.
 */
class GridRowEqualizer {
  #grid;
  #selectors;
  #rafId = null;
  #resizeObserver;
  #mutationObserver;

  constructor(grid, selectors) {
    this.#grid = grid;
    this.#selectors = selectors;

    this.#resizeObserver = new ResizeObserver(() => this.#schedule());
    this.#resizeObserver.observe(grid);

    this.#mutationObserver = new MutationObserver(() => this.#schedule());
    this.#mutationObserver.observe(grid, { childList: true, subtree: true });

    this.#schedule();

    // Re-run after the page fully loads and after web fonts settle. The
    // initial #schedule() can fire before images, fonts, and other deferred
    // resources have applied their final sizes, leaving the equalizer with
    // stale measurements — the symptom is row 1 being stretched taller than
    // its content, which then redistributes as a visible gap between SKU and
    // the price-block separator (because .price-atc-row uses margin-top:auto
    // to pin to the card bottom). Opening DevTools used to mask this by
    // triggering ResizeObserver; now we trigger it explicitly.
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.#schedule(), { once: true });
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.#schedule());
    }
  }

  #schedule() {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      for (const selector of this.#selectors) {
        this.#equalizeSelector(selector);
      }
    });
  }

  #equalizeSelector(selector) {
    const elements = this.#grid.querySelectorAll(selector);
    if (elements.length === 0) return;

    for (const el of elements) {
      el.style.minHeight = '';
    }

    // Group by the containing card's top position, not the target element's
    // own top. Targets like .price-table sit near the bottom of the card and
    // can have varying top positions within the same visual row (e.g., one
    // card has a promo row, pushing its price-table up). Using the card's
    // top keeps cards in the same grid row grouped together.
    const measurements = [];
    for (const el of elements) {
      const card = el.closest('product-card, .product-card, .boost-pfs-filter-product-item') ?? el;
      const top = Math.round(card.getBoundingClientRect().top);
      measurements.push({ el, top, height: el.offsetHeight });
    }

    const rowGroups = new Map();
    const rowMax = new Map();
    for (const { el, top, height } of measurements) {
      let group = rowGroups.get(top);
      if (!group) {
        group = [];
        rowGroups.set(top, group);
        rowMax.set(top, 0);
      }
      group.push(el);
      if (height > rowMax.get(top)) rowMax.set(top, height);
    }

    for (const [top, group] of rowGroups) {
      const max = rowMax.get(top);
      for (const el of group) {
        el.style.minHeight = `${max}px`;
      }
    }
  }

  destroy() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }
}

const EQUALIZE_SELECTORS = [
  '.product-card__title .text-block',
  '.boost-pfs-filter-product-item-title',
  '.price-table',
];

const intersectionObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const grid = entry.target;
      intersectionObserver.unobserve(grid);
      if (!equalizers.has(grid)) {
        equalizers.set(grid, new GridRowEqualizer(grid, EQUALIZE_SELECTORS));
      }
    }
  },
  { rootMargin: '200px' }
);

function discover(root) {
  const grids = root.querySelectorAll?.('.product-grid, .resource-list, .boost-pfs-filter-products');
  if (!grids) return;
  for (const grid of grids) {
    if (!equalizers.has(grid)) {
      intersectionObserver.observe(grid);
    }
  }
}

discover(document);

const pageObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      if (node.matches?.('.product-grid, .resource-list, .boost-pfs-filter-products') && !equalizers.has(node)) {
        intersectionObserver.observe(node);
      }
      discover(node);
    }
  }
});
pageObserver.observe(document.body, { childList: true, subtree: true });
